import { useState, MouseEvent } from 'react';
import { styled } from '@mui/material/styles';
import { Box, ListItemIcon, Typography } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Logo from 'components/Logo';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import BranchSelector from './BranchSelector';
import LanguageSelector from './LanguageSelector';
import WorkCardScanButton from 'features/ergani/workCard/WorkCardScanButton';

const ProfileButton = styled(Button)({
  color: 'inherit',
  boxShadow: 'none',
  textTransform: 'none',
  fontSize: 16,
  backgroundColor: 'initial',
  borderColor: 'none',
  '&:hover': {
    backgroundColor: 'initial',
    borderColor: 'none',
    boxShadow: 'none',
  },
  '&:active': {
    boxShadow: 'none',
    backgroundColor: 'initial',
    borderColor: 'none',
  },
  '&:focus': {
    boxShadow: 'none',
  },
});

const ProfileMenuItem = styled(MenuItem)(({ theme }) => ({
  '&:hover .MuiListItemIcon-root': {
    color: theme.palette.secondary.main,
  },
}));

const Header = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const auth = useAuth();
  const { t } = useTranslation();

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <AppBar
      position="fixed"
      color="primary"
      elevation={0}
      sx={{
        borderBottom: theme => `1px solid ${theme.palette.divider}`,
        zIndex: theme => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ flexWrap: 'wrap', position: 'relative' }} disableGutters>
        <Logo />
        {/* On small screens the logo spans the full width, so the scan shortcut floats at the right edge. */}
        <WorkCardScanButton variant="icon" sx={{ display: { xs: 'inline-flex', md: 'none' }, position: 'absolute', right: 8 }} />
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ display: { xs: 'none', sm: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }} px={2}>
          <WorkCardScanButton variant="header" />
          <BranchSelector variant="header" />
          <LanguageSelector />
          <Box textAlign="right">
            {t('header.welcome')}
            <ProfileButton
              disableRipple
              id="profile-button"
              aria-controls={open ? 'profile-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              onClick={handleClick}
              disableElevation
              endIcon={<KeyboardArrowDown />}
              style={{
                textTransform: 'inherit',
                fontSize: '1rem',
              }}
            >
              <b>{auth.user!!.profile.name}</b>
            </ProfileButton>
            <Menu
              id="profile-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              slotProps={{
                list: {
                  'aria-labelledby': 'profile-button',
                },
              }}
            >
              <ProfileMenuItem
                onClick={() => {
                  handleClose();
                  auth.signoutRedirect({ id_token_hint: auth.user?.id_token });
                }}
              >
                <ListItemIcon>
                  <LogoutOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="inherit">{t('header.logout')}</Typography>
              </ProfileMenuItem>
            </Menu>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
